/**
 * pricingTiers — источник тарифов для PricingOrbit («кольцо» на лендинге).
 *
 * Объединение (2026-06-27): набор/цена/название тарифов берутся из БД
 * (super-admin → Планы, через публичный RPC list_public_plans), а
 * маркетинговые буллеты/CTA/промо — из статики i18n по id плана.
 * Источник истины = кабинет: правишь план в супер-админке → сайт обновляется.
 *
 * Защита: если RPC недоступен (нет ключа / сеть / 4xx) — fetch вернёт null,
 * и resolveTiers отрисует прежнюю статику (сайт не ломается).
 */
import { useEffect, useState } from "react";
import type { Lang } from "../i18n";
import { TIER_ORDER, POPULAR_TIER, CUSTOM_PRICE_TIERS, type TierId } from "../content/pricing";

const SUPABASE_URL =
  (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL) ||
  "https://yaqsyjtgehpvrogthmwo.supabase.co";
const SUPABASE_ANON_KEY =
  (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_ANON_KEY) ||
  "";

/** Активный план из БД (то, что отдаёт list_public_plans). */
export interface PublicPlan {
  id: string;
  name: { ru?: string; kz?: string; en?: string };
  monthly_price: number;
  currency: string;
  display_order: number;
  features?: Record<string, unknown>;
}

/** Тариф, готовый к отрисовке: БД-цена/название + i18n-копирайт. */
export interface ResolvedTier {
  id: string;
  name: string;
  price: string;     // "$25" / "Бесплатно" / "По запросу"
  oldPrice: string;  // "$40" или ""
  currency: string;  // "" — символ валюты уже внутри price
  features: string[];
  cta: string;
  isPopular: boolean;
  isCustom: boolean; // нет фикс-цены → скрыть "/мес"
  glow: string;
}

/** i18n-копирайт одного тарифа (форма из i18n/{ru,kk,en}.ts → pricing.<id>). */
interface TierCopy {
  name?: string;
  price?: string;
  oldPrice?: string;
  currency?: string;
  features?: string[];
  cta?: string;
}

/** Оттенок свечения секции под тариф (фон-якорь). */
const GLOW: Record<string, string> = {
  starter: "rgba(185, 177, 161, 0.10)",
  delivery: "rgba(150, 168, 132, 0.13)",
  pro: "rgba(212, 166, 86, 0.18)",
  enterprise: "rgba(176, 116, 40, 0.14)",
  network: "rgba(212, 166, 86, 0.12)",
};
const GLOW_FALLBACK = "rgba(212, 166, 86, 0.12)";

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$", KZT: "₸", RUB: "₽", EUR: "€", KGS: "сом", UZS: "сум",
};
const FREE_LABEL: Record<Lang, string> = { ru: "Бесплатно", kk: "Тегін", en: "Free" };

/** Лендинг использует "kk" для казахского, в БД ключ — "kz". */
function dbNameKey(lang: Lang): "ru" | "kz" | "en" {
  return lang === "kk" ? "kz" : lang;
}

function formatPrice(amount: number, currency: string, lang: Lang): string {
  if (!amount || amount <= 0) return FREE_LABEL[lang];
  const sym = CURRENCY_SYMBOL[currency] ?? "";
  const grouped = amount.toLocaleString("ru-RU"); // 11 500, не 11500
  // Символьные валюты ($/€/₽) — слева; буквенные (₸/сом/сум) — справа.
  if (sym === "$" || sym === "€" || sym === "₽") return `${sym}${grouped}`;
  return sym ? `${grouped} ${sym}` : grouped;
}

/**
 * Собирает список тарифов для отрисовки. plans !== null → из БД (цена/название/
 * набор/порядок), маркетинг по id из i18n. plans === null → статика (фолбэк =
 * прежнее поведение, включая 5-й «Сеть» с «По запросу»).
 */
export function resolveTiers(
  pricing: Record<string, unknown>,
  lang: Lang,
  plans: PublicPlan[] | null,
): ResolvedTier[] {
  const copyOf = (id: string): TierCopy | undefined => {
    const c = pricing[id];
    return c && typeof c === "object" ? (c as TierCopy) : undefined;
  };
  const contactUs = typeof pricing.contactUs === "string" ? pricing.contactUs : "";
  const popular = POPULAR_TIER as string;

  if (plans && plans.length > 0) {
    return plans.map((p) => {
      const copy = copyOf(p.id);
      const name = p.name[dbNameKey(lang)] || p.name.ru || p.name.en || copy?.name || p.id;
      return {
        id: p.id,
        name,
        price: formatPrice(p.monthly_price, p.currency, lang),
        oldPrice: copy?.oldPrice ?? "",
        currency: "",
        features: copy?.features ?? [],
        cta: copy?.cta ?? contactUs,
        isPopular: p.id === popular,
        isCustom: false, // план из БД всегда с конкретной ценой (или «Бесплатно»)
        glow: GLOW[p.id] ?? GLOW_FALLBACK,
      };
    });
  }

  // Фолбэк: статика как раньше.
  return TIER_ORDER.map((id) => {
    const copy = copyOf(id) ?? {};
    return {
      id,
      name: copy.name ?? id,
      price: copy.price ?? "",
      oldPrice: copy.oldPrice ?? "",
      currency: copy.currency ?? "",
      features: copy.features ?? [],
      cta: copy.cta ?? contactUs,
      isPopular: id === popular,
      isCustom: CUSTOM_PRICE_TIERS.has(id as TierId),
      glow: GLOW[id] ?? GLOW_FALLBACK,
    };
  });
}

/** Один POST к публичному RPC. Любая ошибка → null (сайт упадёт на статику). */
export async function fetchPublicPlans(): Promise<PublicPlan[] | null> {
  if (!SUPABASE_ANON_KEY) return null;
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/list_public_plans`, {
      method: "POST",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        "Content-Type": "application/json",
      },
      body: "{}",
    });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    return Array.isArray(data) ? (data as PublicPlan[]) : null;
  } catch {
    return null;
  }
}

/** Подгружает планы один раз. null = ещё грузится или ошибка (рендерим статику). */
export function usePublicPlans(): PublicPlan[] | null {
  const [plans, setPlans] = useState<PublicPlan[] | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchPublicPlans().then((p) => {
      if (alive) setPlans(p);
    });
    return () => {
      alive = false;
    };
  }, []);
  return plans;
}

/** Сетка цен конструктора тарифов (RPC get_tariff_grid, 00458). null = грузится/ошибка → дефолт из @cafe-os/utils. */
export async function fetchTariffGrid(): Promise<Record<string, number> | null> {
  if (!SUPABASE_ANON_KEY) return null;
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_tariff_grid`, {
      method: "POST",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        "Content-Type": "application/json",
      },
      body: "{}",
    });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    return data && typeof data === "object" && !Array.isArray(data) ? (data as Record<string, number>) : null;
  } catch {
    return null;
  }
}

export function useTariffGrid(): Record<string, number> | null {
  const [grid, setGrid] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchTariffGrid().then((g) => {
      if (alive) setGrid(g);
    });
    return () => {
      alive = false;
    };
  }, []);
  return grid;
}

/** Имя плана из БД по цене (бейдж «= тариф …» в конструкторе); null — нет совпадения. */
export function planNameByPrice(plans: PublicPlan[] | null, price: number, lang: Lang): string | null {
  const p = plans?.find((x) => x.monthly_price === price);
  if (!p) return null;
  return p.name[dbNameKey(lang)] || p.name.ru || p.name.en || p.id;
}
